/**
 * Aspect — Look interaction (center-screen raycast)
 * --------------------------------------------------------------
 * While in pointer-lock walk mode, casts a ray through the screen center each
 * frame to detect doors, structure (walls/windows/floor), and furniture under
 * the crosshair. Fires `look:target-changed` so the crosshair UI can scale up
 * on focus, and `scene:hover-info` for floating spec cards.
 */

import * as pc from "playcanvas";
import {
  windowMeta,
  curtainMeta,
  wallMeta,
  floorMeta,
  ceilingMeta,
  lightMeta,
  switchMeta,
} from "../scene/structure_pricing.js";

function worldAABB(entity) {
  const aabb = new pc.BoundingBox();
  let ok = false;
  for (const render of entity.findComponents("render") ?? []) {
    for (const mi of render.meshInstances ?? []) {
      if (!mi?.aabb) continue;
      if (!ok) {
        aabb.copy(mi.aabb);
        ok = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  return ok ? aabb : null;
}

function rayAABB(origin, dir, aabb) {
  const min = aabb.getMin();
  const max = aabb.getMax();
  let tmin = -Infinity;
  let tmax = Infinity;
  for (const axis of ["x", "y", "z"]) {
    const o = origin[axis];
    const d = dir[axis];
    if (Math.abs(d) < 1e-9) {
      if (o < min[axis] || o > max[axis]) return null;
    } else {
      let t1 = (min[axis] - o) / d;
      let t2 = (max[axis] - o) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tmin = Math.max(tmin, t1);
      tmax = Math.min(tmax, t2);
      if (tmin > tmax) return null;
    }
  }
  return tmax >= 0 ? (tmin >= 0 ? tmin : 0) : null;
}

const STRUCTURE_KINDS = new Set(["wall", "window", "curtain", "floor", "ceiling"]);

export class LookInteraction {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {import('./door_system.js').DoorSystem} doors
   */
  constructor(app, camera, scene, doors, lights = null) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.doors = doors;
    this.lights = lights;
    this._current = null;
    this._hovering = false;
    this._hoverCache = null;
    this._hoverWorld = null;
    this._mouseX = 0;
    this._mouseY = 0;
    this._camMode = "fp";
    this._screenVec = new pc.Vec3();
    this._worldVec = new pc.Vec3();

    const canvas = app.graphicsDevice?.canvas;
    const trackMouse = (e) => {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      this._mouseX = e.clientX - rect.left;
      this._mouseY = e.clientY - rect.top;
    };
    this.app.mouse?.on(pc.EVENT_MOUSEMOVE, (e) => {
      this._mouseX = e.x;
      this._mouseY = e.y;
    });
    document.addEventListener("mousemove", trackMouse);

    this.app.on("fp:lock-changed", ({ locked }) => {
      this._current = null;
      if (locked) this._hovering = false;
      else this._clearHover();
    });
    this.app.on("cam:mode-changed", ({ mode }) => {
      this._camMode = mode ?? "fp";
      this._current = null;
      this._clearHover();
    });
    this.app.on("plan-editor:closed", () => {
      this._current = null;
      this._clearHover();
    });
    this._pickFrame = 0;
    this._onDestroy = () => document.removeEventListener("mousemove", trackMouse);
    this.app.on("update", () => {
      try {
        if (typeof document !== "undefined" && document.body.classList.contains("cinematic-reel")) return;
        if (typeof document !== "undefined" && document.body.classList.contains("revive-open")) return;
        // Phones: 20 Hz pick is enough for hover cards and saves a full scene walk.
        // Screen follow still runs every frame in prerender so the card doesn't stutter.
        if (typeof document !== "undefined" && document.body.classList.contains("touch-ui")) {
          this._pickFrame = (this._pickFrame + 1) % 3;
          if (this._pickFrame !== 0) return;
        }
        this._pickAt(this._pickCoords());
      } catch (err) {
        console.warn("[look] pick failed", err);
      }
    });
    this.app.on("prerender", () => {
      try {
        if (typeof document !== "undefined" && document.body.classList.contains("cinematic-reel")) return;
        if (typeof document !== "undefined" && document.body.classList.contains("revive-open")) return;
        this._reprojectHover();
      } catch (err) {
        console.warn("[look] hover project failed", err);
      }
    });
  }

  _hoverKey(target) {
    if (!target) return "";
    return `${target.kind ?? ""}:${target.id ?? ""}`;
  }

  _canvasRect() {
    return this.app.graphicsDevice?.canvas?.getBoundingClientRect() ?? null;
  }

  /** World point → viewport px; falls back to last screen pos when behind the camera. */
  _screenPos(wx, wy, wz) {
    this._worldVec.set(wx, wy, wz);
    const sv = this.camera.camera.worldToScreen(this._worldVec, this._screenVec);
    const rect = this._canvasRect();
    if (sv.z > 0 && rect) return { x: sv.x + rect.left, y: sv.y + rect.top };
    if (this._hoverCache?.screen) return this._hoverCache.screen;
    return this._pickCoords();
  }

  /**
   * After the camera has moved this frame, re-project the last hovered object's
   * world anchor so the price card tracks walk / gyro at display rate.
   */
  _reprojectHover() {
    if (!this._hovering || !this._hoverCache?.info || !this._canShowHoverPrices()) return;
    const info = this._hoverCache.info;
    const screen = this._hoverWorld
      ? this._screenPos(this._hoverWorld.x, this._hoverWorld.y, this._hoverWorld.z)
      : this._nudgeHoverOffCrosshair(info, this._pickCoords());
    this._hoverCache.screen = { x: screen.x, y: screen.y };
    this.app.fire("scene:hover-info", { info, screen, follow: true });
  }

  _publishHover(info, screen, world = null) {
    if (!info || !screen) return false;
    this._hovering = true;
    const surface = info.category === "floor" || info.category === "ceiling";
    this._hoverWorld = world && !surface ? { x: world.x, y: world.y, z: world.z } : null;
    const placed = this._nudgeHoverOffCrosshair(info, screen);
    this._hoverCache = { info, screen: { x: placed.x, y: placed.y } };
    this.app.fire("scene:hover-info", { info, screen: placed });
    return true;
  }

  /**
   * Floor/ceiling cards must never sit on the center reticle. Always park them
   * beside the look point (room-center projection often coincides with the
   * crosshair when looking up/down).
   */
  _nudgeHoverOffCrosshair(info, screen) {
    const cat = info?.category;
    if (cat !== "floor" && cat !== "ceiling") return screen;

    const pick = this._pickCoords();
    const preferRight = pick.x < window.innerWidth * 0.55;
    const ox = preferRight ? 200 : -200;
    const oy = cat === "ceiling" ? 56 : -56;
    return {
      x: pick.x + ox,
      y: Math.max(120, Math.min(window.innerHeight - 120, pick.y + oy)),
    };
  }

  _clearHover() {
    if (!this._hovering) return;
    this._hovering = false;
    this._hoverCache = null;
    this._hoverWorld = null;
    this.app.fire("scene:hover-info", { info: null });
  }

  _canShowHoverPrices() {
    if (typeof document !== "undefined" && document.body.classList.contains("cinematic-reel")) return true;
    if (this._camMode === "overhead") return true;
    // Phones aim with look-stick / gyro through screen center (no pointer lock).
    if (typeof document !== "undefined" && document.body.classList.contains("touch-ui")) return true;
    const canvas = this.app.graphicsDevice?.canvas;
    return Boolean(canvas && document.pointerLockElement === canvas);
  }

  _pickCoords() {
    const canvas = this.app.graphicsDevice?.canvas;
    const rect = this._canvasRect();
    const locked = Boolean(canvas && document.pointerLockElement === canvas);
    const touchAim =
      typeof document !== "undefined" && document.body.classList.contains("touch-ui");
    if ((locked || touchAim) && rect && canvas) {
      return { x: rect.left + canvas.clientWidth / 2, y: rect.top + canvas.clientHeight / 2 };
    }
    if (rect) return { x: rect.left + this._mouseX, y: rect.top + this._mouseY };
    return { x: this._mouseX, y: this._mouseY };
  }

  _pickStructure(origin, dir) {
    const root = this.scene.roomRoot;
    if (!root) return null;

    let best = null;
    const visit = (entity) => {
      for (const child of entity.children ?? []) {
        const kind = child._aspectKind;
        if (STRUCTURE_KINDS.has(kind)) {
          const aabb = worldAABB(child);
          if (aabb) {
            const t = rayAABB(origin, dir, aabb);
            if (t !== null && (!best || t < best.t)) {
              best = {
                t,
                kind,
                id:
                  kind === "window" || kind === "curtain"
                    ? child._aspectOpeningId
                    : kind === "wall"
                      ? child._aspectWallId
                      : kind === "ceiling"
                        ? "ceiling"
                        : kind === "floor"
                          ? child._aspectRoom?.name
                            ? `floor:${child._aspectRoom.name}`
                            : "floor"
                          : "floor",
                entity: child,
              };
            }
          }
        }
        visit(child);
      }
    };
    visit(root);
    return best;
  }

  _pickAt({ x, y }) {
    const { origin, dir } = this._screenRay(x, y);
    let best = null;
    let bestT = Infinity;
    let hitEntity = null;

    for (const hit of this.doors.interactables()) {
      const aabb = worldAABB(hit.entity);
      if (!aabb) continue;
      const t = rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        best = { kind: hit.kind, id: hit.id };
        hitEntity = hit.entity;
      }
    }

    for (const [id, sw] of this.lights?.switches ?? []) {
      const aabb = worldAABB(sw.plate);
      if (!aabb) continue;
      const t = rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        best = { kind: "switch", id };
        hitEntity = sw.plate;
      }
    }

    for (const hit of this.lights?.interactables?.() ?? []) {
      const aabb = worldAABB(hit.entity);
      if (!aabb) continue;
      const t = rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        best = { kind: "light", id: hit.id, fixture: hit.fixture };
        hitEntity = hit.entity;
      }
    }

    const structure = this._pickStructure(origin, dir);
    if (structure && structure.t < bestT) {
      bestT = structure.t;
      best = { kind: structure.kind, id: structure.id };
      hitEntity = structure.entity;
    }

    for (const [id, entity] of this.scene.entities) {
      if (!entity.enabled) continue;
      const aabb = worldAABB(entity);
      if (!aabb) continue;
      const t = rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        best = { kind: "furniture", id };
        hitEntity = entity;
      }
    }

    if (best?.id !== this._current?.id || best?.kind !== this._current?.kind) {
      this._current = best;
      this._emit(best);
    }

    if (!this._canShowHoverPrices()) {
      this._clearHover();
      return;
    }

    if (!best) {
      this._clearHover();
      return;
    }

    let hoverEmitted = false;
    if (best.kind === "furniture") {
      hoverEmitted = this._emitHoverInfo(best.id);
    } else if (best.kind === "door") {
      hoverEmitted = this._emitDoorInfo(best.id);
    } else if (best.kind === "window") {
      hoverEmitted = this._emitWindowInfo(best.id, hitEntity);
    } else if (best.kind === "curtain") {
      hoverEmitted = this._emitCurtainInfo(best.id, hitEntity);
    } else if (best.kind === "wall") {
      hoverEmitted = this._emitWallInfo(best.id, hitEntity);
    } else if (best.kind === "floor") {
      hoverEmitted = this._emitFloorInfo(hitEntity);
    } else if (best.kind === "ceiling") {
      hoverEmitted = this._emitCeilingInfo(hitEntity);
    } else if (best.kind === "light") {
      hoverEmitted = this._emitLightInfo(best.id, best.fixture, hitEntity);
    } else if (best.kind === "switch") {
      hoverEmitted = this._emitSwitchInfo(hitEntity);
    }

    if (!hoverEmitted && this._hoverCache?.info) {
      const cat = this._hoverCache.info.category;
      // Keep the last card tracking its world anchor — never snap to the cursor.
      if (cat !== "floor" && cat !== "ceiling") {
        this._reprojectHover();
      }
    }
  }

  _emitSpecCard(entity, info, opts = {}) {
    if (!entity || !info) return false;
    const offset = opts.offset ?? 0.2;
    const surface = opts.surface ?? "top";
    const wp = entity.getPosition();
    const scale = entity.getLocalScale();
    const half = scale?.y ? scale.y / 2 : 1;
    const baseY = opts.anchorY ?? wp.y;
    const anchorY =
      opts.anchorY != null
        ? surface === "bottom"
          ? baseY - offset
          : baseY + offset
        : surface === "bottom"
          ? baseY - half - offset
          : baseY + half + offset;
    const anchorX = opts.anchorX ?? wp.x;
    const anchorZ = opts.anchorZ ?? wp.z;
    const world = { x: anchorX, y: anchorY, z: anchorZ };
    const screen = this._screenPos(anchorX, anchorY, anchorZ);
    return this._publishHover(info, screen, world);
  }

  /** Stable XZ anchor for whole-room floor/ceiling cards (not the ray hit or cursor). */
  _roomFootprintCenter(room) {
    const poly = room?.floorPolygon ?? room?.floors?.[0]?.polygon;
    if (Array.isArray(poly) && poly.length >= 3) {
      let sx = 0;
      let sz = 0;
      for (const [x, z] of poly) {
        sx += Number(x) || 0;
        sz += Number(z) || 0;
      }
      return { x: sx / poly.length, z: sz / poly.length };
    }
    const b = room?.bounds;
    if (b?.min && b?.max) {
      return { x: (b.min[0] + b.max[0]) / 2, z: (b.min[2] + b.max[2]) / 2 };
    }
    return { x: 0, z: 0 };
  }

  _emitDoorInfo(id) {
    const meta = this.doors.meta?.(id);
    if (!meta?.entity) return false;
    const cm = (v) => Math.round(v * 100);
    return this._emitSpecCard(meta.entity, {
      id,
      name: meta.name ?? "Door",
      brand: meta.brand ?? null,
      category: "door",
      material: meta.material ?? null,
      price: meta.price ?? null,
      dims: meta.width && meta.height ? `${cm(meta.width)}cm x ${cm(meta.height)}cm` : null,
    });
  }

  _emitWindowInfo(id, entity) {
    const op = this.scene.room?.openings?.find((o) => o.id === id);
    if (!op || !entity) return false;
    const meta = windowMeta(op);
    const cm = (v) => Math.round(v * 100);
    return this._emitSpecCard(entity, {
      id,
      name: meta.name,
      brand: meta.brand,
      category: "window",
      material: meta.material,
      price: meta.price,
      dims: `${cm(meta.width)}cm x ${cm(meta.height)}cm`,
    });
  }

  _emitCurtainInfo(id, entity) {
    const op = this.scene.room?.openings?.find((o) => o.id === id);
    if (!op || !entity) return false;
    const meta = curtainMeta(op);
    const cm = (v) => Math.round(v * 100);
    return this._emitSpecCard(entity, {
      id: `curtain:${id}`,
      name: meta.name,
      brand: meta.brand,
      category: "curtain",
      material: meta.material,
      price: meta.price,
      dims: `${cm(meta.width)}cm x ${cm(meta.height)}cm`,
    });
  }

  _emitWallInfo(id, entity) {
    const wall = this.scene.room?.walls?.find((w) => w.id === id);
    if (!wall || !entity) return false;
    const meta = wallMeta(wall, this.scene.room);
    const cm = (v) => Math.round(v * 100);
    return this._emitSpecCard(entity, {
      id,
      name: meta.name,
      brand: meta.brand,
      category: "wall",
      material: meta.material,
      price: meta.price,
      dims: `${cm(meta.length)}cm x ${cm(meta.height)}cm`,
    });
  }

  _emitFloorInfo(entity) {
    if (!entity || !this.scene.room) return false;
    const home = this.scene.room;
    const hit = this._floorHitXZ();
    const zone = entity._aspectRoom ?? (hit ? this._subRoomAt(hit.x, hit.z) : null);
    const meta = floorMeta(home, zone);
    const cm = (v) => Math.round(v * 100);
    const dims =
      meta.width > 0 && meta.depth > 0
        ? `${cm(meta.width)}cm × ${cm(meta.depth)}cm`
        : meta.area > 0
          ? `${meta.area.toFixed(1)} m²`
          : null;
    const id = zone?.name ? `floor:${zone.name}` : "floor";
    // Park beside the reticle (floor category is nudged in _publishHover).
    return this._publishHover(
      {
        id,
        name: meta.name,
        brand: meta.brand,
        category: "floor",
        material: meta.material,
        price: meta.price,
        dims,
        finishId: meta.finishId,
      },
      this._pickCoords(),
    );
  }

  /** World XZ under the crosshair on the active floor plane. */
  _floorHitXZ() {
    const { x, y } = this._pickCoords();
    const { origin, dir } = this._screenRay(x, y);
    const floorY = this.scene.room?.floorY ?? 0;
    if (Math.abs(dir.y) < 1e-8) return { x: origin.x, z: origin.z };
    const t = (floorY - origin.y) / dir.y;
    if (!Number.isFinite(t) || t < 0) return { x: origin.x, z: origin.z };
    return { x: origin.x + dir.x * t, z: origin.z + dir.z * t };
  }

  /** Named sub-room under a world XZ point (apartment local rooms). */
  _subRoomAt(wx, wz) {
    const home = this.scene.room;
    const rooms = home?.rooms;
    if (!Array.isArray(rooms) || !rooms.length) return null;
    const min = home.bounds?.min ?? [0, 0, 0];
    let best = null;
    let bestArea = Infinity;
    for (const r of rooms) {
      const rw = Number(r.width) || 0;
      const rd = Number(r.depth) || 0;
      if (rw < 0.2 || rd < 0.2) continue;
      const x0 = min[0] + (Number(r.x) || 0);
      const z0 = min[2] + (Number(r.z) || 0);
      if (wx < x0 || wx > x0 + rw || wz < z0 || wz > z0 + rd) continue;
      const area = rw * rd;
      if (area < bestArea) {
        bestArea = area;
        best = {
          name: r.name ?? r.type ?? "Room",
          type: r.type ?? "",
          x: Number(r.x) || 0,
          z: Number(r.z) || 0,
          width: rw,
          depth: rd,
        };
      }
    }
    return best;
  }

  _emitCeilingInfo(entity) {
    if (!entity || !this.scene.room) return false;
    const room = this.scene.room;
    const meta = ceilingMeta(room);
    const cm = (v) => Math.round(v * 100);
    const dims =
      meta.width > 0 && meta.depth > 0
        ? `${cm(meta.width)}cm x ${cm(meta.depth)}cm · ${meta.height.toFixed(1)}m high`
        : meta.area > 0
          ? `${meta.area.toFixed(1)} m²`
          : null;
    const center = this._roomFootprintCenter(room);
    const ceilY = (room.floorY ?? 0) + (room.dimensions?.height ?? 2.8);
    return this._emitSpecCard(
      entity,
      {
        id: "ceiling",
        name: meta.name,
        brand: meta.brand,
        category: "ceiling",
        material: meta.material,
        price: meta.price,
        dims,
      },
      { offset: 0.08, surface: "bottom", anchorX: center.x, anchorZ: center.z, anchorY: ceilY },
    );
  }

  _emitLightInfo(id, fixture, entity) {
    if (!entity) return false;
    const meta = lightMeta(fixture);
    return this._emitSpecCard(
      entity,
      {
        id,
        name: meta.name,
        brand: meta.brand,
        category: "light",
        material: meta.material,
        price: meta.price,
        dims: null,
      },
      { offset: 0.1, surface: "bottom" },
    );
  }

  _emitSwitchInfo(entity) {
    const meta = switchMeta();
    return this._emitSpecCard(entity, {
      id: "switch",
      name: meta.name,
      brand: meta.brand,
      category: "switch",
      material: meta.material,
      price: meta.price,
      dims: null,
    }, { offset: 0.08, surface: "top" });
  }

  _emitHoverInfo(id) {
    const rec = this.scene.placements?.get(id);
    const entity = this.scene.entities?.get(id);
    if (!rec || !entity) return false;
    const meta = this.scene.catalog?.get(rec.asset_id) ?? {};
    const fp = rec.footprint ?? meta.footprint;
    const scale = rec.scale ?? 1;

    const wp = entity.getPosition();
    const topY = (rec.position?.[1] ?? wp.y) + (fp ? fp.h * scale : 0.8) + 0.2;
    const world = { x: wp.x, y: topY, z: wp.z };
    const screen = this._screenPos(world.x, world.y, world.z);

    const cm = (v) => Math.round(v * scale * 100);
    return this._publishHover(
      {
        id,
        name: meta.name ?? rec.asset_id,
        brand: meta.brand ?? null,
        category: meta.category ?? null,
        material: meta.material ?? null,
        price: rec.price ?? meta.price ?? null,
        dims: fp ? `${cm(fp.w)}cm x ${cm(fp.d)}cm` : null,
      },
      screen,
      world,
    );
  }

  _screenRay(x, y) {
    const cam = this.camera?.camera;
    if (!cam || typeof cam.screenToWorld !== "function") {
      return { origin: new pc.Vec3(), dir: new pc.Vec3(0, 0, -1) };
    }
    const near = cam.screenToWorld(x, y, cam.nearClip);
    const far = cam.screenToWorld(x, y, cam.farClip);
    const dir = far.clone().sub(near).normalize();
    return { origin: near, dir };
  }

  _emit(target) {
    this.app.fire("look:target-changed", {
      kind: target?.kind ?? null,
      id: target?.id ?? null,
      active: Boolean(target),
    });
  }
}

export default LookInteraction;
